(() => {
  'use strict';
  if (window.milaNativePdpLoaded) return;
  window.milaNativePdpLoaded = true;
  const controllers = new Map();
  const additions = new WeakMap();
  const messages = {
    en: { adding: 'Adding…', updating: 'Updating…', added: 'Added ✓', refresh: 'Refresh cart', addError: 'We could not add this item. Please try again.', refreshError: 'Your item was added, but the cart could not be refreshed. Refresh the cart to see it.' },
    fr: { adding: 'Ajout…', updating: 'Mise à jour…', added: 'Ajouté ✓', refresh: 'Actualiser le panier', addError: 'Impossible d’ajouter cet article. Réessayez.', refreshError: 'Votre article a été ajouté, mais le panier n’a pas pu être actualisé. Actualisez le panier pour le voir.' },
    de: { adding: 'Wird hinzugefügt…', updating: 'Wird aktualisiert…', added: 'Hinzugefügt ✓', refresh: 'Warenkorb aktualisieren', addError: 'Dieser Artikel konnte nicht hinzugefügt werden. Versuche es erneut.', refreshError: 'Dein Artikel wurde hinzugefügt, aber der Warenkorb konnte nicht aktualisiert werden. Aktualisiere den Warenkorb, um ihn zu sehen.' },
    it: { adding: 'Aggiunta…', updating: 'Aggiornamento…', added: 'Aggiunto ✓', refresh: 'Aggiorna il carrello', addError: 'Impossibile aggiungere questo articolo. Riprova.', refreshError: 'Il tuo articolo è stato aggiunto, ma non è stato possibile aggiornare il carrello. Aggiornalo per visualizzarlo.' }
  };
  const t = messages[(document.documentElement.lang || 'en').slice(0, 2)] || messages.en;
  const cartURL = path => (window.Shopify?.routes?.root || '/') + path;
  const readJSON = response => { if (!response.ok) throw new Error('cart'); return response.json(); };
  const setText = (node, value) => { if (node && node.textContent !== value) node.textContent = value; };

  async function refreshCart() {
    const cart = await fetch(cartURL('cart.js'), { headers: { Accept: 'application/json' } }).then(readJSON);
    if (!cart || !Array.isArray(cart.items) || typeof cart.item_count !== 'number') throw new Error('cart');
    document.dispatchEvent(new CustomEvent('cart:change', { bubbles: true, detail: { cart } }));
    return cart;
  }

  function showError(button, text) {
    let error = button.nextElementSibling;
    if (!error?.matches('[data-native-error]')) {
      error = document.createElement('p'); error.dataset.nativeError = '';
      error.setAttribute('role', 'alert'); error.tabIndex = -1;
      error.style.cssText = 'color:#a32824;font-size:.875rem;line-height:1.5;margin:10px 0';
      button.after(error);
    }
    error.textContent = text; error.focus();
  }

  async function addExtra(button) {
    const id = Number(button.dataset.variantId);
    if (!id || button.disabled) return;
    let state = additions.get(button);
    if (!state) { state = { busy: false, added: false, original: button.innerHTML }; additions.set(button, state); }
    if (state.busy || button.classList.contains('is-done')) return;
    state.busy = true; button.disabled = true; button.classList.add('is-loading'); button.setAttribute('aria-busy', 'true');
    if (button.nextElementSibling?.matches('[data-native-error]')) button.nextElementSibling.remove();
    setText(button, state.added ? t.refresh : t.adding);
    try {
      if (!state.added) {
        const added = await fetch(cartURL('cart/add.js'), { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ items: [{ id, quantity: 1, properties: { _origen: 'landing-addon' } }] }) }).then(readJSON);
        const items = added && (added.items || (added.id ? [added] : []));
        if (!Array.isArray(items) || !items.some(item => item && Number(item.variant_id || item.id) === id && item.quantity > 0)) throw new Error('add');
        // Persist success before reading cart.js. A read failure must never repeat the add.
        state.added = true;
      }
      await refreshCart();
      button.classList.add('is-done');
      setText(button, button.dataset.labelDone || t.added);
    } catch (error) {
      if (state.added) setText(button, t.refresh);
      else { button.innerHTML = state.original; refreshCart().catch(() => {}); }
      showError(button, state.added ? t.refreshError : t.addError);
    } finally {
      state.busy = false; button.disabled = button.classList.contains('is-done');
      button.classList.remove('is-loading'); button.removeAttribute('aria-busy');
    }
  }

  function init() {
    for (const [root, controller] of controllers) if (!root.isConnected) { controller.cleanup(); controllers.delete(root); }
    document.querySelectorAll('[data-mila-native-pdp]').forEach(root => {
      if (controllers.has(root)) return;
      let cfg;
      try { cfg = JSON.parse(root.querySelector('[data-native-config]').textContent); } catch { return; }
      if (!Array.isArray(cfg.variants)) return;
      const variants = new Map(cfg.variants.map(variant => [Number(variant.id), variant]));
      let observedForm, observedPicker, frame;
      const observer = new MutationObserver(() => { cancelAnimationFrame(frame); frame = requestAnimationFrame(sync); });
      function main() {
        const forms = [...document.querySelectorAll('.product-info form[action*="/cart/add"]'), ...document.querySelectorAll('form[action*="/cart/add"]')];
        const form = forms.find(candidate => variants.has(Number(candidate.querySelector('[name="id"]')?.value)));
        const picker = form && [...document.querySelectorAll('variant-picker')].find(candidate => candidate.getAttribute('form-id') === form.getAttribute('id'));
        return { form, picker };
      }
      function sync() {
        const { form, picker } = main();
        if (form !== observedForm || picker !== observedPicker) {
          observer.disconnect(); observedForm = form; observedPicker = picker;
          if (form) observer.observe(form, { subtree: true, attributes: true, attributeFilter: ['disabled', 'aria-busy'] });
          if (picker) observer.observe(picker, { attributes: true, attributeFilter: ['aria-busy'] });
        }
        const variant = variants.get(Number(form?.querySelector('[name="id"]')?.value || cfg.selectedVariantId));
        const available = !!variant?.available;
        const pending = picker?.getAttribute('aria-busy') === 'true';
        const adding = [...(form?.elements || [])].some(element => element.type === 'submit' && element.getAttribute('aria-busy') === 'true');
        root.querySelectorAll('[data-native-choose]').forEach(button => {
          const label = button.querySelector('[data-native-label]');
          if (!button.dataset.nativeAvailableLabel) button.dataset.nativeAvailableLabel = label?.textContent.trim() || '';
          const text = !available ? cfg.soldOut : pending ? t.updating : adding ? t.adding : button.dataset.nativeAvailableLabel;
          button.disabled = !available || pending || adding;
          setText(label, text);
          const choice = [variant?.title === 'Default Title' ? '' : variant?.title, variant?.price].filter(Boolean).join(' · ');
          setText(button.querySelector('[data-native-choice]'), choice ? ' · ' + choice : '');
          button.setAttribute('aria-label', text + (choice ? ' · ' + choice : ''));
        });
        root.querySelectorAll('[data-native-price]').forEach(node => setText(node, variant?.price || ''));
        root.querySelectorAll('[data-native-compare]').forEach(node => { node.hidden = !variant?.compare; setText(node, variant?.compare || ''); });
        if (variant?.id) root.querySelectorAll('[data-pdp-reviews]').forEach(link => {
          const url = new URL(link.href, document.baseURI); url.searchParams.set('variant', variant.id);
          if (link.href !== url.href) link.href = url.href;
        });
      }
      function choose() {
        const { form, picker } = main();
        if (picker?.getAttribute('aria-busy') === 'true') return;
        const buy = form?.closest('.product-info') || form;
        const target = picker?.querySelector('select:not(:disabled), button:not(:disabled), input[data-option-position]:not(:disabled)') || buy?.querySelector('[data-pw-open]:not(:disabled), button[type="submit"]:not(:disabled)') || picker || buy;
        if (!target) return;
        if (!target.matches('input,select,button,a,[tabindex]')) target.tabIndex = -1;
        (picker || target).scrollIntoView({ behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth', block: 'center' });
        target.focus({ preventScroll: true });
      }
      controllers.set(root, { sync, choose, cleanup() { observer.disconnect(); cancelAnimationFrame(frame); } });
      sync();
    });
    controllers.forEach(controller => controller.sync());
  }
  document.addEventListener('click', event => {
    const addon = event.target.closest('[data-native-addon]');
    if (addon) { event.preventDefault(); addExtra(addon); return; }
    const choose = event.target.closest('[data-native-choose]');
    if (choose && !choose.disabled) { event.preventDefault(); controllers.get(choose.closest('[data-mila-native-pdp]'))?.choose(); }
  });
  document.addEventListener('variant:change', init);
  document.addEventListener('change', event => { if (event.target.matches('form[action*="/cart/add"] [name="id"]')) init(); });
  document.addEventListener('product:rerender', () => queueMicrotask(init), true);
  document.addEventListener('shopify:section:load', init);
  document.addEventListener('shopify:section:unload', () => queueMicrotask(init));
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true }); else init();
})();
